import type { DocumentSubject } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { flattenAliasHighlights } from 'features/knowledgeDocumentViewer/utils/aliasHighlights'

/**
 * Where a subject is referenced on one document: every drawable box on the
 * first page that has any, plus the later pages that also reference it.
 */
export type SubjectReferenceTarget = {
  pageNumber: number
  boxes: NormalizedBBox[]
  morePages: number[]
}

/**
 * Collapse a subject's alias boxes on a document into a navigation target.
 * Returns null when nothing is drawable (no subject, or references persisted
 * without coordinates) — callers fall back to opening the document plain.
 */
export function collectSubjectReferenceTarget(
  subject: DocumentSubject | undefined
): SubjectReferenceTarget | null {
  if (!subject) return null
  const boxesByPage = new Map<number, NormalizedBBox[]>()
  for (const highlight of flattenAliasHighlights([subject])) {
    const boxes = boxesByPage.get(highlight.page)
    if (boxes) boxes.push(highlight.bbox)
    else boxesByPage.set(highlight.page, [highlight.bbox])
  }
  if (boxesByPage.size === 0) return null
  const [firstPage, ...morePages] = [...boxesByPage.keys()].sort(
    (a, b) => a - b
  )
  return {
    pageNumber: firstPage,
    boxes: boxesByPage.get(firstPage) ?? [],
    morePages,
  }
}
